import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

/*
 * FestivalDrillDown — modal showing the full month festival list.
 * Same shell pattern as HoraDrillDown.
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => p.theme.colors.modal?.backdrop || 'rgba(0,0,0,0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${p => p.theme.spacing[4]}px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    padding: 0;
  }
`;

const ModalBox = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.radius.md}px;
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${p => p.theme.shadow.modal || p.theme.shadow.card};

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing[3]}px ${p => p.theme.spacing[4]}px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: ${p => p.theme.typography.weights.semibold};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: ${p => p.theme.colors.text.secondary};
  padding: 4px 8px;
  min-width: 44px;
  min-height: 44px;

  &:hover { color: ${p => p.theme.colors.text.primary}; }
  &:focus {
    outline: 2px solid ${p => p.theme.colors.bands.exceptional};
    outline-offset: 2px;
  }
`;

const FestivalTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }

  thead th {
    background: ${p => p.theme.colors.background};
    font-weight: ${p => p.theme.typography.weights.semibold};
    color: ${p => p.theme.colors.text.secondary};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  tbody td.mono {
    font-family: ${p => p.theme.typography.monoFamily};
    color: ${p => p.theme.colors.text.secondary};
    white-space: nowrap;
  }

  tbody td.tag {
    color: ${p => p.theme.colors.text.muted};
    font-size: 12px;
  }
`;

const Empty = styled.div`
  padding: ${p => p.theme.spacing[5]}px;
  text-align: center;
  color: ${p => p.theme.colors.text.muted};
`;

function getFocusable(el) {
  if (!el) return [];
  return Array.from(el.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ));
}

function formatDate(dateStr, lang) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(lang === 'ta' ? 'ta-IN' : 'en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch (e) {
    return dateStr;
  }
}

const FestivalDrillDown = ({ open, onClose, festivals = [], lang = 'en' }) => {
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = getFocusable(modalRef.current);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    setTimeout(() => {
      const focusable = getFocusable(modalRef.current);
      if (focusable.length > 0) focusable[0].focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Backdrop onClick={onClose}>
      <ModalBox
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={lang === 'ta' ? 'இந்த மாத பண்டிகைகள்' : 'Festivals This Month'}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            {lang === 'ta' ? 'இந்த மாத பண்டிகைகள்' : 'Festivals This Month'}
          </ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label={lang === 'ta' ? 'மூடு' : 'Close'}>
            ×
          </CloseButton>
        </ModalHeader>
        {festivals && festivals.length > 0 ? (
          <FestivalTable>
            <thead>
              <tr>
                <th>{lang === 'ta' ? 'பண்டிகை' : 'Festival'}</th>
                <th>{lang === 'ta' ? 'தேதி' : 'Date'}</th>
                <th>{lang === 'ta' ? 'வகை' : 'Type'}</th>
              </tr>
            </thead>
            <tbody>
              {festivals.map((f, i) => (
                <tr key={i}>
                  <td>{lang === 'ta' ? (f.name_ta || f.name) : f.name}</td>
                  <td className="mono">{formatDate(f.date, lang)}</td>
                  <td className="tag">{f.type || ''}</td>
                </tr>
              ))}
            </tbody>
          </FestivalTable>
        ) : (
          <Empty>
            {lang === 'ta' ? 'இந்த மாதம் பண்டிகைகள் இல்லை' : 'No festivals this month'}
          </Empty>
        )}
      </ModalBox>
    </Backdrop>
  );
};

export default FestivalDrillDown;
